"use client";

import { useEffect } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { usePostHog } from "posthog-js/react";

const CAPTURED_CANCELLATION_PREFIX = "posthog_checkout_cancelled";

export default function CheckoutCancellationTracker(): null {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const posthog = usePostHog();

  useEffect(() => {
    if (!posthog || searchParams.get("checkout_cancelled") !== "true") {
      return;
    }

    const checkoutAttemptId =
      searchParams.get("checkout_attempt_id") || "unknown";
    const storageKey = `${CAPTURED_CANCELLATION_PREFIX}:${checkoutAttemptId}`;

    const captureCancellation = () => {
      let alreadyCaptured = false;
      try {
        alreadyCaptured = window.sessionStorage.getItem(storageKey) === "1";
      } catch {
        // Continue without storage-based deduplication.
      }

      if (!alreadyCaptured) {
        posthog.capture("checkout_cancelled", {
          checkout_attempt_id: checkoutAttemptId,
          checkout_flow: searchParams.get("checkout_flow") || undefined,
          checkout_place: searchParams.get("checkout_place") || undefined,
          package_type: searchParams.get("package_type") || undefined,
          platform: "web",
        });
        try {
          window.sessionStorage.setItem(storageKey, "1");
        } catch {
          // The event was still captured if storage is unavailable.
        }
      }

      const nextParams = new URLSearchParams(searchParams.toString());
      nextParams.delete("checkout_cancelled");
      nextParams.delete("checkout_attempt_id");
      nextParams.delete("checkout_flow");
      nextParams.delete("checkout_place");
      nextParams.delete("package_type");
      const nextUrl = nextParams.size
        ? `${pathname}?${nextParams.toString()}`
        : pathname;
      router.replace(nextUrl, { scroll: false });
    };

    if (posthog.__loaded) {
      captureCancellation();
      return;
    }

    window.addEventListener(
      "guestgallery:posthog-loaded",
      captureCancellation,
      { once: true }
    );
    return () => {
      window.removeEventListener(
        "guestgallery:posthog-loaded",
        captureCancellation
      );
    };
  }, [pathname, posthog, router, searchParams]);

  return null;
}
